import type React from 'react';
import InfoTooltip from '@/components/common/InfoTooltip';
import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  getReschedulePolicyWindow,
  formatReschedulePolicyWindowRange,
} from './reschedulePolicyWindow';
import { useWidth } from '@/hooks/useWidth';

// IDs de los paneles de configuración de horarios (WorkSchedulePanel, SpecificDayPanel,
// BlockSchedulePanel) que se acoplan a la izquierda de la grilla en desktop. Ninguno de
// los tres se monta a la vez.
const LEFT_PANEL_IDS = ['work-schedule-panel', 'specific-day-panel', 'block-schedule-panel'];

/**
 * Mide en vivo el borde derecho del panel de configuración de horarios (si hay alguno
 * montado) para que el banner pueda correrse y no taparle el botón "Guardar cambios".
 * Solo aplica en desktop: en mobile esos paneles son un drawer full-width con z-index
 * mayor al del banner, así que ya lo tapan sin necesidad de reposicionar nada.
 */
function useLeftPanelClearance(enabled: boolean): number | null {
  const [clearance, setClearance] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) { setClearance(null); return; }

    const measure = () => {
      const panel = LEFT_PANEL_IDS
        .map((id) => document.getElementById(id))
        .find((el): el is HTMLElement => !!el);
      setClearance(panel ? panel.getBoundingClientRect().right : null);
    };

    measure();
    const resizeObserver = new ResizeObserver(measure);
    LEFT_PANEL_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) resizeObserver.observe(el);
    });
    // Los paneles se montan/desmontan condicionalmente (no solo cambian de tamaño),
    // así que además hay que observar el DOM para detectar esas transiciones.
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', measure);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled]);

  return clearance;
}

interface RescheduleSlot {
  date: string;
  dayLabel: string;
  startTime: string;
  endTime: string;
}

interface CalendarBannerProps {
  rescheduleMinutes: number;
  rescheduleSlotsNextWeek?: RescheduleSlot[];
  weekKey: string;
  visible: boolean;
  onEditModeToggle?: () => void;
  /** Called when "Configuralos ahora" is clicked on banner1. Opens the reschedule summary modal. */
  onConfigureReschedule?: () => void;
  lowHours7Days?: { minutes: number; visible: boolean };
  /** Shown with top priority when the psychologist has no fixed/recurring schedule loaded. */
  noTemplate?: { visible: boolean };
}

const fmtH = (m: number) => `${Math.floor(m / 60)}h${m % 60 > 0 ? ` ${m % 60}min` : ''}`;

const CalendarBanner = ({
  rescheduleMinutes,
  rescheduleSlotsNextWeek = [],
  weekKey,
  visible,
  onEditModeToggle,
  onConfigureReschedule,
  lowHours7Days,
  noTemplate,
}: CalendarBannerProps) => {
  const [bannerEntered, setBannerEntered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { size } = useWidth();
  const isMobile = size === 's';
  const leftPanelClearance = useLeftPanelClearance(!isMobile);

  const banner0ShouldShow = !!noTemplate?.visible;
  const banner1ShouldShow = !banner0ShouldShow && visible;
  const banner2ShouldShow = !banner0ShouldShow && !banner1ShouldShow && !!lowHours7Days?.visible;
  const activeBanner = banner0ShouldShow ? 'banner0' : banner1ShouldShow ? 'banner1' : banner2ShouldShow ? 'banner2' : null;

  useEffect(() => {
    if (!activeBanner) { setBannerEntered(false); return; }
    setBannerEntered(false);
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setBannerEntered(true));
    });
    return () => cancelAnimationFrame(raf);
  }, [activeBanner, weekKey]);

  useEffect(() => {
    setExpanded(false);
  }, [activeBanner, weekKey]);

  if (!activeBanner) return null;

  const missingMins = Math.max(0, 600 - (lowHours7Days?.minutes ?? 0));
  const missingLabel = fmtH(missingMins);

  const rescheduleRequired = 240; // 4 h
  const rescheduleMissing = Math.max(0, rescheduleRequired - rescheduleMinutes);

  const isRedBanner = activeBanner === 'banner1' && rescheduleMinutes === 0;

  // Calculado en tiempo real con el mismo utilitario que el modal "Tus horarios
  // de reprogramación", para que el rango mostrado nunca se desincronice del
  // realmente usado para calcular las horas disponibles.
  const policyWindowRange = formatReschedulePolicyWindowRange(getReschedulePolicyWindow());

  // Cuando hay un panel de configuración de horarios abierto a la izquierda (Horario fijo,
  // Día específico o Bloqueo), corremos el banner a la derecha de ese panel en vez de
  // centrarlo en todo el viewport, para que nunca le tape el botón "Guardar cambios".
  const panelAvoidanceStyle: React.CSSProperties | undefined =
    leftPanelClearance != null
      ? {
          left: leftPanelClearance + 16,
          right: '1rem',
          transform: 'none',
          width: 'auto',
          minWidth: 0,
          maxWidth: `calc(100vw - ${leftPanelClearance + 16}px - 1rem)`,
        }
      : undefined;

  return (
    <div
      className={`fixed left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] sm:w-[70%] sm:min-w-[36rem] pointer-events-none transition-all duration-[400ms] ease-in-out ${
        !bannerEntered ? '-bottom-20 opacity-0' : 'bottom-20 sm:bottom-6 opacity-100'
      }`}
      style={panelAvoidanceStyle}
    >
      <div className={`flex flex-col gap-0 px-4 py-3 rounded-xl border text-xs font-medium shadow-lg pointer-events-auto ${
        isRedBanner
          ? 'bg-red-50 border-red-300 text-red-800'
          : 'bg-amber-50 border-amber-200 text-amber-700'
      }`}>
        {/* Main row */}
        <div className="flex items-center gap-2">
          <span className={`flex-1 ${isRedBanner ? 'font-bold' : ''}`}>
            {activeBanner === 'banner0' ? (
              'No tenés ningún horario fijo cargado. Hacelo desde Configurar horarios → Horario fijo.'
            ) : activeBanner === 'banner1' ? (
              rescheduleMinutes === 0
                ? 'No tenés horarios de reprogramación para los próximos 7 días. Habilitá al menos 4 h.'
                : (<>
                    {`Tenés ${fmtH(rescheduleMinutes)} disponible en los próximos 7 días. Necesitás al menos 4h en días y turnos distintos. Te faltan ${fmtH(rescheduleMissing)}.`}
                    <InfoTooltip
                      text={`Se suman todos tus horarios disponibles —Estándar y Reprogramación— en el período contemplado: ${policyWindowRange}. Necesitás al menos 4 horas distribuidas en días y turnos distintos para cumplir con nuestras políticas de asignación de pacientes.`}
                      iconColor="#f59e0b"
                      placement="top"
                      style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: '0.25rem' }}
                    />
                  </>)
            ) : (
              `¿Querés recibir más pacientes? Sumá al menos ${missingLabel} más a tu agenda y maximizá tus chances.`
            )}
          </span>

          {activeBanner === 'banner1' && (
            <button
              type="button"
              data-testid="reschedule-slots-toggle"
              onClick={() => setExpanded(e => !e)}
              className="shrink-0 flex items-center gap-1 whitespace-nowrap py-1 px-2 rounded-lg text-xs font-medium border border-current opacity-70 hover:opacity-100 bg-transparent cursor-pointer transition-opacity"
              aria-expanded={expanded}
            >
              {expanded ? 'Ocultar' : 'Ver horarios'}
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          )}

          <button
            type="button"
            data-testid="calendar-banner-cta-btn"
            onClick={() => activeBanner === 'banner1'
              ? onConfigureReschedule?.()
              : onEditModeToggle?.()
            }
            className={`shrink-0 whitespace-nowrap py-1.5 px-3 rounded-lg text-xs font-semibold border-none cursor-pointer transition-colors ${
              isRedBanner
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-amber-500 text-white hover:bg-amber-600'
            }`}
          >
            {/* banner1 abre el modal informativo "Tus horarios de reprogramación";
                banner0 y banner2 activan directamente el modo de edición de horarios. */}
            {activeBanner === 'banner0' ? 'Configurar horarios' : activeBanner === 'banner1' ? 'Más información' : 'Configuralos ahora'}
          </button>
        </div>

        {/* Expandable slot list */}
        {activeBanner === 'banner1' && expanded && (
          <div
            data-testid="reschedule-slots-list"
            className="mt-2 pt-2 border-t border-current border-opacity-20 flex flex-col gap-1"
          >
            {rescheduleSlotsNextWeek.length === 0 ? (
              <span className="opacity-70 italic">No tenés horarios de reprogramación configurados en este período.</span>
            ) : (
              rescheduleSlotsNextWeek.map((slot, idx) => (
                <span key={`${slot.date}-${slot.startTime}-${idx}`} className="opacity-80">
                  {slot.dayLabel} · {slot.startTime}–{slot.endTime}
                </span>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarBanner;
